import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Play,
  Download,
  Trash2,
  Archive,
  ArchiveRestore,
  FileText,
  Volume2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Brain,
  InfinityIcon,
  type LucideIcon,
} from "lucide-react";
import { type TranscriptionData } from "@/redux/api/transcriptions.rtkq";
import { useState } from "react";
import SummaryViewer from "./SummaryViewer";
import SummaryGenerationForm from "./SummaryGenerationForm";
import { isStuckTranscription } from "./transcriptionStale";
import { useDeleteTranscriptionMutation } from "@/redux/api/transcriptions.rtkq";
import { useDeleteFileMutation, useLazyGetFileURLQuery } from "@/redux/api/documents.rtkq";
import { useAuthContext } from "@/hooks/useAuthContext";
import { toast } from "sonner";
import { ref, deleteObject } from "firebase/storage";
import { deleteDoc, deleteField, doc, updateDoc } from "firebase/firestore";
import { db, storage } from "@/lib/firebase";

const transcriptionDocRef = (userId: string, transcriptionId: string) =>
  doc(db, "users", userId, "transcriptions", transcriptionId);

// Action button configuration
interface ActionButtonConfig {
  icon: LucideIcon;
  tooltip: string;
  onClick: (transcription: TranscriptionData) => void;
  disabled?: boolean;
  variant?: "default" | "destructive";
  actionType?: "view" | "download" | "play" | "summary" | "delete" | "restore";
}

// Reusable action button component
interface ActionButtonProps {
  config: ActionButtonConfig;
  transcription: TranscriptionData;
  isCompleted: boolean;
  isReadOnly?: boolean;
}

const ActionButton = ({ config, transcription, isCompleted, isReadOnly = false }: ActionButtonProps) => {
  const { icon: Icon, tooltip, onClick, disabled = false, variant = "default", actionType = "view" } = config;

  const requiresCompleted = actionType !== "delete" && actionType !== "restore";
  const isDisabled = disabled || (requiresCompleted && !isCompleted);
  // For read-only mode, block destructive actions but allow viewing, downloading, and playing.
  const isActionDisabled =
    isDisabled || (isReadOnly && (actionType === "delete" || actionType === "restore" || actionType === "summary"));
  const buttonClass = variant === "destructive" ? "h-8 w-8 p-0 hover:bg-red-50" : "h-8 w-8 p-0 hover:bg-blue-50";
  const iconClass = variant === "destructive" ? "h-4 w-4 text-red-600" : "h-4 w-4 text-blue-600";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onClick(transcription)}
          className={buttonClass}
          disabled={isActionDisabled}
          aria-label={tooltip}
          title={tooltip}
        >
          <Icon className={iconClass} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
};

// TranscriptionRow component for individual transcription rows
interface TranscriptionRowProps {
  transcription: TranscriptionData;
  onViewTranscription: (transcription: TranscriptionData) => void;
  onTranscriptionRestored?: () => void;
  isReadOnly?: boolean;
}

const TranscriptionRow = ({ transcription, onViewTranscription, onTranscriptionRestored, isReadOnly = false }: TranscriptionRowProps) => {
  const [isSummaryViewerOpen, setIsSummaryViewerOpen] = useState(false);
  const [isSummaryFormOpen, setIsSummaryFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState<"soft" | "hard">("soft");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  const { user } = useAuthContext();
  const [deleteTranscription] = useDeleteTranscriptionMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [triggerGetUrl, { data: audioData, isFetching: isFetchingAudioUrl, error: audioUrlError }] = useLazyGetFileURLQuery();

  const hasSummary = transcription.summary && transcription.summary.summaryStatus === "completed";
  const isSummaryProcessing = transcription.summary?.summaryStatus === "processing";
  const isCompleted = transcription.processing.status === "completed";
  const isDeleted = transcription.isDeleted;
  // Client clocks cannot determine server failure. Staleness only prompts a status check.
  const isStuck = isStuckTranscription(transcription);
  const displayStatus = transcription.processing.status;
  const statusError = transcription.processing.error;

  // Handler functions
  const handleViewTranscription = () => {
    onViewTranscription(transcription);
  };

  const handleDownload = (transcription: TranscriptionData) => {
    console.log("Download transcription:", transcription.fileName);
    // You can implement download logic here
    const blob = new Blob([transcription.transcription], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${transcription.fileName}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePlay = async (transcription: TranscriptionData) => {
    if (!user?.uid) {
      toast.error("עליך להתחבר תחילה");
      return;
    }

    try {
      const pathParam = transcription.storagePath.startsWith(`${user.uid}/`)
        ? transcription.storagePath.replace(`${user.uid}/`, "")
        : transcription.storagePath;

      if (!audioData?.downloadURL) {
        await triggerGetUrl({ path: pathParam, userId: user.uid }).unwrap();
      }
      setIsPlayerOpen(true);
    } catch (e) {
      console.error("Failed to fetch audio URL", e);
      toast.error("שגיאה בטעינת קובץ השמע");
    }
  };

  const handleConfirmDelete = async () => {
    if (!user?.uid) return;

    setIsDeleting(true);

    try {
      const transcriptionRef = transcriptionDocRef(user.uid, transcription.id);

      if (deleteType === "soft") {
        await updateDoc(transcriptionRef, {
          isDeleted: true,
          deletedAt: new Date().toISOString(),
        });
      } else {
        await deleteDoc(transcriptionRef);

        try {
          const filePath = transcription.storagePath;
          if (filePath.startsWith(`${user.uid}/`)) {
            await deleteObject(ref(storage, filePath));
          } else {
            await deleteFile({
              path: filePath,
              userId: user.uid,
            }).unwrap();
          }
        } catch (fileError) {
          console.warn("Failed to delete audio file:", fileError);
        }

        try {
          await deleteTranscription({
            userId: user.uid,
            transcriptionId: transcription.id,
            deleteType: "hard",
          }).unwrap();
        } catch (backendError) {
          console.warn("Backend delete failed after Firestore update:", backendError);
        }
      }

      toast.success(deleteType === "soft" ? "התמלול הועבר לסל המחזור" : "התמלול נמחק בהצלחה");

      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting transcription:", error);
      toast.error("שגיאה במחיקת התמלול");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteDialog = () => {
    setDeleteType(isDeleted ? "hard" : "soft");
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteType("soft");
  };

  const handleRestore = async () => {
    if (!user?.uid) {
      toast.error("עליך להתחבר תחילה");
      return;
    }

    try {
      await updateDoc(transcriptionDocRef(user.uid, transcription.id), {
        isDeleted: false,
        deletedAt: deleteField(),
      });

      toast.success("התמלול שוחזר בהצלחה");
      onTranscriptionRestored?.();
    } catch (error) {
      console.error("Error restoring transcription:", error);
      toast.error("שגיאה בשחזור התמלול");
    }
  };

  const handleSummaryAction = () => {
    if (hasSummary) {
      setIsSummaryViewerOpen(true);
    } else {
      setIsSummaryFormOpen(true);
    }
  };

  const handleSummaryGenerated = (summaryId: string) => {
    // Summary was generated, the query will automatically refetch
    console.log("Summary generated:", summaryId);
  };

  const failedActionButtons: ActionButtonConfig[] = [];

  const completedActionButtons: ActionButtonConfig[] = [
    {
      icon: FileText,
      tooltip: "צפה בתמלול",
      onClick: handleViewTranscription,
      disabled: isSummaryProcessing,
      actionType: "view",
    },
    {
      icon: Brain,
      tooltip: hasSummary ? "צפה בסיכום" : "צור סיכום",
      onClick: handleSummaryAction,
      disabled: isSummaryProcessing,
      actionType: "summary",
    },
    {
      icon: Play,
      tooltip: "נגן קובץ",
      onClick: handlePlay,
      actionType: "play",
    },
    {
      icon: Download,
      tooltip: "הורד תמלול",
      onClick: handleDownload,
      actionType: "download",
    },
  ];

  const statusActionButtons = displayStatus === "failed" ? failedActionButtons : completedActionButtons;

  const actionButtons: ActionButtonConfig[] = isDeleted
    ? [
        {
          icon: ArchiveRestore,
          tooltip: "שחזר תמלול",
          onClick: handleRestore,
          disabled: isDeleting,
          actionType: "restore",
        },
        {
          icon: Trash2,
          tooltip: "מחק לצמיתות",
          onClick: openDeleteDialog,
          disabled: isDeleting,
          variant: "destructive",
          actionType: "delete",
        },
      ]
    : [
        ...statusActionButtons,
        {
          icon: Archive,
          tooltip: "העבר לסל המחזור",
          onClick: openDeleteDialog,
          disabled: isDeleting,
          actionType: "delete",
        },
      ];
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (durationInSecondsAndMs: number) => {
    if (durationInSecondsAndMs === Infinity) return <InfinityIcon className="w-4 h-4 text-gray-500" />;
    const durationInSeconds = Math.floor(durationInSecondsAndMs);
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 shrink-0 text-green-500" aria-hidden />;
      case "processing":
        return isDeleted ? (
          <Clock className="w-4 h-4 shrink-0 text-gray-500" aria-hidden />
        ) : (
          <Loader2 className="w-4 h-4 shrink-0 text-blue-500 animate-spin" aria-hidden />
        );
      case "failed":
        return <XCircle className="w-4 h-4 shrink-0 text-destructive" aria-hidden />;
      default:
        return <Clock className="w-4 h-4 shrink-0 text-gray-500" aria-hidden />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "הושלם";
      case "processing":
        return "מעבד";
      case "failed":
        return "נכשל";
      default:
        return "ממתין";
    }
  };

  const dialogs = (
    <>
      {transcription.summary && (
        <SummaryViewer
          summary={transcription.summary}
          transcriptionId={transcription.id}
          userId={transcription.userId}
          isOpen={isSummaryViewerOpen}
          onOpenChange={setIsSummaryViewerOpen}
        />
      )}

      <SummaryGenerationForm
        transcription={transcription}
        isOpen={isSummaryFormOpen}
        onOpenChange={setIsSummaryFormOpen}
        onSummaryGenerated={handleSummaryGenerated}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) setDeleteType("soft");
        }}
      >
        <AlertDialogContent translate="no">
          <AlertDialogHeader>
            <AlertDialogTitle>{isDeleted ? "מחיקה לצמיתות" : "העברה לסל המחזור"}</AlertDialogTitle>
            <AlertDialogDescription className="break-all">
              {isDeleted
                ? `התמלול "${transcription.fileName}" יימחק לצמיתות יחד עם קובץ השמע. פעולה זו לא ניתנת לביטול.`
                : `התמלול "${transcription.fileName}" יועבר לסל המחזור וניתן יהיה לשחזר אותו מאוחר יותר.`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {isDeleted && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">⚠️ מחיקה לצמיתות תסיר גם את קובץ השמע.</p>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete} disabled={isDeleting}>
              ביטול
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className={isDeleted ? "bg-red-600 hover:bg-red-700" : ""}
            >
              <span className="relative size-4 shrink-0" aria-hidden>
                {isDeleted ? (
                  <Trash2 className={`absolute inset-0 size-4 ${isDeleting ? "invisible" : ""}`} />
                ) : (
                  <Archive className={`absolute inset-0 size-4 ${isDeleting ? "invisible" : ""}`} />
                )}
                <Loader2 className={`absolute inset-0 size-4 animate-spin ${isDeleting ? "" : "invisible"}`} />
              </span>
              <span className={isDeleting ? "hidden" : ""}>
                {isDeleted ? "מחק לצמיתות" : "העבר לסל המחזור"}
              </span>
              <span className={isDeleting ? "" : "hidden"}>{isDeleted ? "מוחק..." : "מעביר..."}</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isPlayerOpen} onOpenChange={setIsPlayerOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-chen-900 text-right break-all">ניגון קובץ שמע</DialogTitle>
            <DialogDescription className="text-right">{transcription.summary?.title || transcription.fileName}</DialogDescription>
          </DialogHeader>

          {isFetchingAudioUrl ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              טוען קובץ שמע...
            </div>
          ) : audioUrlError ? (
            <div className="text-destructive text-center py-6">שגיאה בטעינת קובץ השמע</div>
          ) : audioData?.downloadURL ? (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground text-right">
                משך: {formatDuration(transcription.metadata.duration)}
              </div>
              <audio src={audioData.downloadURL} controls className="w-full" autoPlay />
            </div>
          ) : (
            <div className="text-muted-foreground text-center py-6">אין כתובת להורדת קובץ</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  return (
      <TableRow className={`hover:bg-muted/50 ${isDeleted ? "bg-muted/30" : ""}`}>
        {/* File Name Column */}
        <TableCell className="text-right">
          <div className="space-y-2">
            <div className={`font-medium ${isDeleted ? "line-through text-muted-foreground" : "text-chen-900"}`}>
              {transcription.summary?.title || transcription.fileName}
            </div>
            {(isCompleted || isDeleted || hasSummary || isSummaryProcessing) && (
            <div className="flex flex-wrap gap-2">
              {isCompleted && (
                <>
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                    <FileText className="w-3 h-3 ml-1" />
                    תמלול
                  </Badge>
                  <Badge variant="secondary" className="bg-green-50 text-green-700 hover:bg-green-100">
                    <Volume2 className="w-3 h-3 ml-1" />
                    {transcription.metadata.language === "he" ? "עברית" : "English"}
                  </Badge>
                </>
              )}
              {isDeleted && (
                <Badge variant="destructive" className="bg-red-50 text-red-700 hover:bg-red-100">
                  <Archive className="w-3 h-3 ml-1" />
                  נמחק
                </Badge>
              )}
              {hasSummary && (
                <Badge variant="secondary" className="bg-purple-50 text-purple-700 hover:bg-purple-100">
                  <Brain className="w-3 h-3 ml-1" />
                  סיכום זמין
                </Badge>
              )}
              {isSummaryProcessing && (
                <Badge variant="secondary" className="bg-gray-50 text-gray-700">
                  <Loader2 className="w-3 h-3 ml-1 animate-spin" />
                  מעבד סיכום...
                </Badge>
              )}
            </div>
            )}
          </div>
        </TableCell>

        {/* Date Column */}
        <TableCell className="text-right">
          <div className="text-foreground">{formatDate(transcription.createdAt)}</div>
        </TableCell>

        {/* Duration Column */}
        <TableCell className="text-right">
          <div className="text-foreground">{formatDuration(transcription.metadata.duration)}</div>
        </TableCell>

        {/* Status Column */}
        <TableCell className="align-top text-right max-w-56 whitespace-normal overflow-hidden">
          <div className="flex min-w-0 flex-col items-end gap-1.5">
            <div className="flex items-center gap-2 whitespace-nowrap">
              {getStatusIcon(displayStatus)}
              <span className={displayStatus === "failed" ? "text-destructive font-medium" : "text-foreground"}>
                {getStatusText(displayStatus)}
              </span>
            </div>
            {isStuck && (
              <p className="text-xs text-muted-foreground" role="status">ממתין לעדכון מצב מהשרת</p>
            )}
            {displayStatus === "failed" && statusError && (
              <p className="max-w-full text-xs text-destructive text-right leading-snug break-words" role="alert">
                {statusError}
              </p>
            )}
          </div>
        </TableCell>

        {/* Actions Column */}
        <TableCell className="align-top text-right whitespace-nowrap">
          <TooltipProvider>
            <div className="flex items-center gap-2">
              {actionButtons.map((config, index) => (
                <ActionButton key={index} config={config} transcription={transcription} isCompleted={isCompleted} isReadOnly={isReadOnly} />
              ))}
            </div>
          </TooltipProvider>
          {dialogs}
        </TableCell>
      </TableRow>
  );
};

export default TranscriptionRow;
